import React, { useEffect, useState } from "react";
import { User, Lock, FloppyDisk, Spinner, CheckCircle, Lifebuoy, X } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import SubscriptionCard from "@/components/SubscriptionCard";
import BusinessProfileCard from "@/components/BusinessProfileCard";
import CustomSenderSection from "@/components/CustomSenderSection";
import DeliverabilityBadge from "@/components/DeliverabilityBadge";
import ReferralCard from "@/components/ReferralCard";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  // One-time password-recovery tip — dismissible, sticks via localStorage
  const [showRecoveryTip, setShowRecoveryTip] = useState(
    () => typeof window !== "undefined" && localStorage.getItem("cf_pw_recovery_tip_dismissed") !== "1",
  );
  const dismissRecoveryTip = () => {
    try { localStorage.setItem("cf_pw_recovery_tip_dismissed", "1"); } catch {}
    setShowRecoveryTip(false);
  };

  const saveName = async () => {
    if (!name.trim() || name.trim() === user?.name) return;
    setSavingName(true);
    try {
      await api.patch("/auth/profile", { name: name.trim() });
      toast.success("Name updated");
      await refresh?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    } finally {
      setSavingName(false);
    }
  };

  const changePassword = async (e) => {
    e?.preventDefault();
    if (!currentPw || !newPw) {
      toast.error("Fill all fields");
      return;
    }
    if (newPw.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("Passwords don't match");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast.success("Password updated", { description: "Use the new password next time you log in." });
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Password change failed");
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <AppShell>
      <PageHeader
        eyebrow="Profile"
        title="Your account"
        subtitle="Update your display name and change your password."
        testid="profile-header"
      />

      <div className="px-4 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-4xl">
        <BusinessProfileCard onSaved={refresh} />
        <SubscriptionCard />
        <DeliverabilityBadge />
        {/* Profile card */}
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <User size={16} weight="duotone" className="text-orange-500" />
            <div className="font-display font-medium text-zinc-100">Profile</div>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Email</Label>
              <Input
                value={user?.email || ""}
                disabled
                data-testid="profile-email"
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-500 font-mono"
              />
              <div className="text-[10px] font-mono text-zinc-600 mt-1">Email cannot be changed.</div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Display name</Label>
              <Input
                data-testid="profile-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1.5 bg-zinc-950 border-zinc-800"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Role</Label>
              <div className="mt-1.5 text-sm text-zinc-300 font-mono" data-testid="profile-role">
                {user?.role === "admin" ? (
                  <span className="text-orange-400">ADMIN</span>
                ) : (
                  <span className="text-zinc-400">USER</span>
                )}
              </div>
            </div>
            <Button
              data-testid="profile-save-btn"
              onClick={saveName}
              disabled={savingName || !name.trim() || name.trim() === user?.name}
              className="w-full bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm"
            >
              {savingName ? <><Spinner size={14} className="mr-1.5 animate-spin" /> Saving…</> : <><FloppyDisk size={14} className="mr-1.5" /> Save name</>}
            </Button>
          </div>
        </div>

        {/* Password card */}
        <form
          onSubmit={changePassword}
          data-testid="password-form"
          className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5"
        >
          <div className="flex items-center gap-2 mb-4">
            <Lock size={16} weight="duotone" className="text-orange-500" />
            <div className="font-display font-medium text-zinc-100">Change password</div>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Current password</Label>
              <Input
                type="password"
                data-testid="current-password-input"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                autoComplete="current-password"
                className="mt-1.5 bg-zinc-950 border-zinc-800"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">New password (min 6 chars)</Label>
              <Input
                type="password"
                data-testid="new-password-input"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                autoComplete="new-password"
                className="mt-1.5 bg-zinc-950 border-zinc-800"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Confirm new password</Label>
              <Input
                type="password"
                data-testid="confirm-password-input"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                autoComplete="new-password"
                className="mt-1.5 bg-zinc-950 border-zinc-800"
              />
              {confirmPw && newPw && confirmPw === newPw && (
                <div className="text-[10px] font-mono text-emerald-400 mt-1 flex items-center gap-1">
                  <CheckCircle size={10} weight="fill" /> Passwords match
                </div>
              )}
            </div>
            <Button
              type="submit"
              data-testid="change-password-btn"
              disabled={savingPw || !currentPw || !newPw || !confirmPw}
              className="w-full bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm"
            >
              {savingPw ? <><Spinner size={14} className="mr-1.5 animate-spin" /> Updating…</> : <><Lock size={14} className="mr-1.5" /> Update password</>}
            </Button>
          </div>
        </form>
        {showRecoveryTip && (
          <div
            data-testid="pw-recovery-tip"
            className="lg:col-span-2 bg-orange-500/[0.06] border border-orange-500/30 rounded-sm p-4 flex items-start gap-3"
          >
            <Lifebuoy size={18} weight="duotone" className="text-orange-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-orange-200 font-medium">Locked out later? You've got a backup.</div>
              <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
                If you ever forget your password, head to{" "}
                <a
                  href="/forgot-password"
                  data-testid="pw-recovery-tip-link"
                  className="font-mono text-orange-400 hover:underline underline-offset-2"
                >
                  /forgot-password
                </a>{" "}
                — a reset link is emailed straight to <span className="font-mono text-zinc-300">{user?.email}</span>. Bookmark this page and you're covered.
              </div>
            </div>
            <button
              type="button"
              onClick={dismissRecoveryTip}
              data-testid="pw-recovery-tip-dismiss"
              className="text-zinc-500 hover:text-zinc-200 shrink-0"
              aria-label="Dismiss tip"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
        )}
        <ReferralCard />
        <div className="lg:col-span-2">
          <CustomSenderSection />
        </div>
      </div>
    </AppShell>
  );
}
